import { useState, type ChangeEvent, type KeyboardEvent } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import {
  DiscountContainer,
  DiscountHeader,
  DiscountIcon,
  IconLine,
  DiscountTitle,
  InputButtonWrapper,
  DiscountInput,
  ApplyButton,
  CodeWrapper,
  SuccessIcon,
  AppliedText,
  AppliedCode,
  RemoveButton,
  ErrorMessage,
} from './styles';

interface DiscountProps {
  additionalParams: any;
  errors?: any;
  discountApplied: boolean;
  discountLoading: boolean;
  discountCode?: string;
  onApplyDiscount: (params: any) => void;
  onRemoveDiscount: (params: any) => void;
}

export const Discount = ({
  additionalParams,
  errors,
  discountApplied,
  discountLoading,
  discountCode,
  onApplyDiscount,
  onRemoveDiscount,
}: DiscountProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState(discountCode || '');
  const [clientSideErrors, setClientSideErrors] = useState<string[]>([]);
  const intl = useIntl();
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    setClientSideErrors([]);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setClientSideErrors([]);
  };

  const applyDiscountCode = () => {
    const code = inputValue.trim();

    if (!code) {
      setClientSideErrors([
        intl.formatMessage({
          defaultMessage: 'This field cannot be blank',
          description: 'Error message for empty discount code input',
        }),
      ]);
      return;
    }

    if (!discountLoading) {
      setClientSideErrors([]);
      onApplyDiscount({
        ...additionalParams,
        discount_code: code,
      });
    }
  };

  const handleApply = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    applyDiscountCode();
  };

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!discountLoading) {
      e.preventDefault();
      e.stopPropagation();
      onRemoveDiscount(additionalParams);
    }
    setClientSideErrors([]);
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      applyDiscountCode();
    }
  };

  const getErrorMessages = () => {
    if (clientSideErrors.length > 0) {
      return clientSideErrors;
    }
    if (errors && errors.toJS) {
      return errors.toJS();
    }
    return [];
  };

  const errorMessages = getErrorMessages();

  return (
    <DiscountContainer
      className={`DiscountCodeContainer ${isExpanded ? 'expanded' : ''}`}
    >
      {discountApplied ? (
        <div
          className="DiscountCode-applied"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <SuccessIcon className="icon af-checkmark brand-bg" />
          <AppliedText className="copy">
            {intl.formatMessage({
              defaultMessage: 'Promo code applied',
              description:
                'Success message indicating that a promotional/discount code has been successfully applied to the booking',
            })}
          </AppliedText>
          <CodeWrapper className="code">
            <AppliedCode>{inputValue}</AppliedCode>
            <RemoveButton
              className="DiscountCode-remove brand-color"
              data-testid="discount-remove"
              onClick={handleRemove}
            >
              {intl.formatMessage({
                defaultMessage: 'Remove',
                description:
                  'Button text to remove the applied promotional/discount code',
              })}
            </RemoveButton>
          </CodeWrapper>
        </div>
      ) : (
        <>
          <DiscountHeader className="DiscountCode-Title">
            <DiscountIcon
              data-testid="discount-expander"
              isExpanded={isExpanded}
              onClick={handleToggle}
            >
              <IconLine isExpanded={isExpanded} />
            </DiscountIcon>
            <DiscountTitle>
              {intl.formatMessage({
                defaultMessage: 'Redeem Promo Code',
                description: 'Label for the redeem promo code section',
              })}
            </DiscountTitle>
          </DiscountHeader>
          <div
            style={{
              display: isExpanded ? 'block' : 'none',
              marginTop: '12px',
            }}
          >
            <InputButtonWrapper
              className={`DiscountCode-Input row ${isExpanded ? 'expanded' : ''}`}
            >
              <DiscountInput
                data-testid="discount-input"
                type="text"
                placeholder={intl.formatMessage({
                  defaultMessage: 'Enter Promo Code',
                  description:
                    'Placeholder text for the promotional/discount code input field',
                })}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={discountLoading}
                hasError={errorMessages.length > 0}
                className="DiscountCode-Input"
              />
              <ApplyButton
                type="button"
                data-testid="discount-apply"
                className={`ui ${discountLoading ? 'loading' : ''} button`}
                onClick={handleApply}
                isLoading={discountLoading}
                disabled={discountLoading}
              >
                {discountLoading ? (
                  <FormattedMessage
                    defaultMessage="Loading"
                    description="Loading state for the apply button"
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Apply"
                    description="Button text to apply/submit a promotional/discount code"
                  />
                )}
              </ApplyButton>
            </InputButtonWrapper>

            {errorMessages.length > 0 && (
              <ErrorMessage data-testid="discount-error">
                {errorMessages.join(', ')}
              </ErrorMessage>
            )}
          </div>
        </>
      )}
    </DiscountContainer>
  );
};
